import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogService } from 'primeng/dynamicdialog';
import { FieldsetModule } from 'primeng/fieldset';
import { FileUploadModule } from 'primeng/fileupload';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { Company } from 'src/app/company/company.model';
import { FreightForwarder } from 'src/app/company/freight-forwarder/freight-forwarder.model';
import { ContractsService } from 'src/app/contracts/contracts.service';
import { Customer } from 'src/app/customer/customer.model';
import { CustomerService } from 'src/app/customer/customer.service';
import { Location } from 'src/app/location/location.model';
import { LazyLocationPickerDirective } from 'src/app/shared/location-picker/lazy-location-picker.directive';
import { City } from 'src/app/models/common/city.model';
import { Country } from 'src/app/models/common/country.model';
import { PartyService } from 'src/app/party/party.service';
import { PartyType } from 'src/app/party/party-type.enum';
import { FirebaseStorageService } from 'src/app/services/firebase-storage.service';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { BaseMasterDataService } from '../../services/base-master-data.service';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { LogisticsProjectsService } from '../../logistics-projects/logistics-projects.service';
import { LogisticsProjectLookupComponent } from '../../logistics-projects/logistics-project-lookup.component';
import { PartyQuickCreateDialogComponent, PartyQuickCreateResult, PartyQuickCreateType } from '../../shared/party-quick-create/party-quick-create-dialog.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { CommercialPartyPickerComponent } from '../../shared/commercial-party-picker/commercial-party-picker.component';
import { CommercialPartyReference } from '../../shared/commercial-party-picker/commercial-party.model';
import { CustomsClearanceEndpointService } from '../clearance.service';
import { ClearanceStatus } from '../models/clearance-status.enum';
import { CustomsClearanceDetail } from '../models/customs-clearance.detail';
import { CustomsClearanceFileTypes } from '../models/customs-clearance-file-type';
import { CustomsClearanceFile } from '../models/customs-clearance-file.model';
import { CustomsClearanceHeader } from '../models/customs-clearance-header.model';
import { CustomsContainer } from '../models/customs-container.model';
import { Shipper } from '../models/shipper.model';

@Component({
    selector: 'app-add-edit-clearance',
    templateUrl: './add-edit-clearance.component.html',
    styleUrls: ['./add-edit-clearance.component.css'],
    standalone: true,
    imports: [
        LogisticsProjectLookupComponent,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        ToastModule,
        ProgressBarModule,
        DatePickerModule,
        SelectModule,
        LazyLocationPickerDirective,
        ButtonModule,
        FieldsetModule,
        InputTextModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        MultiSelectModule,
        FileUploadModule,
        TableModule,
        PartyQuickCreateDialogComponent,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent,
        CommercialPartyPickerComponent
    ],
    providers: [
        MessageService,
        CustomsClearanceEndpointService,
        SessionStorageService,
        ContractsService,
        CustomerService,
        DialogService,
        BaseMasterDataService,
        PartyService
    ]
})
export class AddEditClearanceComponent implements OnInit {

    commercialPartyReference: CommercialPartyReference = { source: 'CRM' };

    canCreateClearance = true;
    canEditClearance = true;
    canManageClearance = true;
    canUploadClearanceFile = true;
    canManageCharges = true;

    clearanceForm: FormGroup;
    clearanceDetails: CustomsClearanceDetail[] = [];

    isEditMode = false;
    isLoading = false;
    uploadProgress = 0;
    uploadProgressByType: Record<string, number> = {};
    uploadedFiles: CustomsClearanceFile[] = [];
    clearanceStatuses: { label: string, value: ClearanceStatus }[] = [];

    shippers: Shipper[] = [];
    customsBrokers: FreightForwarder[] = [];
    countries: Country[] = [];
    loadingCities: City[] = [];
    dischargeCities: City[] = [];
    customsCities: City[] = [];
    shipperContacts: Customer[] = [];
    consigneeContacts: Customer[] = [];
    consignees: Company[] = [];
    incoterms: any[] = [];
    customsClearanceFileTypes = CustomsClearanceFileTypes;

    filteredShipperContacts: Customer[] = [];
    filteredConsigneeContacts: Customer[] = [];
    filteredConsignees: Company[] = [];

    quickCreateVisible = false;
    quickCreateType: PartyQuickCreateType | null = null;

    constructor(
        private clearanceService: CustomsClearanceEndpointService,
        private messageService: MessageService,
        private router: Router,
        private contractService: ContractsService,
        private route: ActivatedRoute,
        private customerService: CustomerService,
        private sessionStorageService: SessionStorageService,
        private baseMasterDataService: BaseMasterDataService,
        private partyService: PartyService,
        private logisticsProjectsService: LogisticsProjectsService,
        private fb: FormBuilder,
        private firebaseStorageService: FirebaseStorageService,
        private permissionService: PermissionService
    ) {
        this.clearanceForm = this.fb.group({});
    }

    ngOnInit(): void {
        this.canCreateClearance = this.permissionService.hasPermission(app_permissions.clearance.records.create);
        this.canEditClearance = this.permissionService.hasPermission(app_permissions.clearance.records.edit);
        this.canUploadClearanceFile = this.permissionService.hasPermission(app_permissions.clearance.files.upload);
        this.canManageCharges = this.permissionService.hasPermission(app_permissions.clearance.charges.edit);
        this.canManageClearance = this.canCreateClearance || this.canEditClearance;
        this.loadCountries();
        this.buildForm();
        this.loadConsignees();
        this.loadShippers();
        this.loadShipperContacts();
        this.loadCustomsBrokers();
        this.addContainerRow();
        this.wireFormChanges();

        const preselectedProjectId = Number(this.route.snapshot.queryParamMap.get('projectId') || '') || null;
        this.route.params.subscribe(params => {
            const id = params['id'];
            if (id) {
                this.isEditMode = true;
                this.loadClearanceHeader(id);
            } else {
                this.clearanceForm.patchValue({
                    owningCompanyId: this.sessionStorageService.getCompany()!.id,
                    logisticsProjectId: preselectedProjectId
                });
            }
        });
    }

    private buildForm(): void {
        this.clearanceForm = this.fb.group({
            clearanceHeaderID: [null],
            owningCompanyId: [this.sessionStorageService.getCompany()?.id ?? null, Validators.required],
            owningCompanyContactId: [this.sessionStorageService.getCustomer()?.id ?? null],
            logisticsProjectId: [null],
            clearanceDate: [new Date(), Validators.required],
            broker: [null, Validators.required],
            client: [null],
            crmClientId: [null],
            crmAccountId: [null],
            crmClientContactId: [null],
            clientContactId: [null],
            shipper: [null, Validators.required],
            shipperContact: [null],
            consignee: [null, Validators.required],
            consigneeContact: [null, Validators.required],
            assignedTo: [null],
            status: [ClearanceStatus.DRAFT],
            primaryContractNumber: [''],
            subContractNumber: [''],
            bl_awb_number: [''],
            hbl_number: [''],
            bayanNumber: [''],
            clearanceJobNumber: [''],
            deliveryOrderNumber: [''],
            jobNumber: [''],
            incoterm: [null],
            vesselName: [''],
            country: [null],
            loadingCountryId: [null, Validators.required],
            loadingCityId: [null, Validators.required],
            dischargeCountryId: [null, Validators.required],
            dischargeCityId: [null, Validators.required],
            customsCountryId: [null, Validators.required],
            customsCityId: [null, Validators.required],
            arrivalLocation: [''],
            pol: [''],
            pod: [''],
            eta: [null],
            etd: [null],
            deliveryLocation: [null, Validators.required],
            containers: [''],
            containerType: [''],
            customsLocation: [''],
            customsContainers: this.fb.array([]),
            clearanceFiles: this.fb.array([]),
            selectedFileTypes: [[]]
        });

        this.clearanceStatuses = Object.keys(ClearanceStatus).map(key => ({ label: key, value: ClearanceStatus[key as keyof typeof ClearanceStatus] }));
        this.incoterms = [
            { name: 'EXW (Ex Works)' },
            { name: 'FCA (Free Carrier)' },
            { name: 'CPT (Carriage Paid To)' },
            { name: 'CIP (Carriage and Insurance Paid To)' },
            { name: 'DAP (Delivered at Place)' },
            { name: 'DPU (Delivered at Place Unloaded)' },
            { name: 'DDP (Delivered Duty Paid)' },
            { name: 'FAS (Free Alongside Ship)' },
            { name: 'FOB (Free on Board)' },
            { name: 'CFR (Cost and Freight)' },
            { name: 'CIF (Cost, Insurance and Freight)' }
        ];
    }

    private wireFormChanges(): void {
        this.clearanceForm.get('consignee')?.valueChanges.subscribe(company => {
            if (company) {
                this.customerService.getCustomersByCompany(company.id).subscribe(customers => {
                    this.consigneeContacts = customers;
                });
            }
        });

        this.clearanceForm.get('deliveryLocation')?.valueChanges.subscribe(location => {
            if (location && location.defaultContact) {
                this.clearanceForm.get('consigneeContact')?.patchValue(location.defaultContact);
            }
        });

        this.clearanceForm.get('loadingCountryId')?.valueChanges.subscribe(countryId => this.onRouteCountryChange('loading', countryId));
        this.clearanceForm.get('dischargeCountryId')?.valueChanges.subscribe(countryId => this.onRouteCountryChange('discharge', countryId));
        this.clearanceForm.get('customsCountryId')?.valueChanges.subscribe(countryId => this.onRouteCountryChange('customs', countryId));
    }

    loadConsignees(): void {
        const companyId = this.sessionStorageService.getCompany()!.id;
        this.contractService.getContractRelatedCompanies(companyId).subscribe({
            next: value => this.consignees = value,
            error: error => {
                console.log(error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fetching Contract companies failed',
                    detail: 'Unable to load consignee companies.'
                });
            }
        });
    }

    loadClearanceHeader(id: number): void {
        this.isLoading = true;
        this.clearanceService.getCustomsClearanceHeaderById(id).subscribe({
            next: (data) => {
                this.clearanceForm.patchValue(data);
                this.commercialPartyReference = {
                    source: data.crmClientId ? 'CRM' : 'LEGACY',
                    crmClientId: data.crmClientId,
                    crmAccountId: data.crmAccountId,
                    crmContactId: data.crmClientContactId,
                    legacyCompanyId: data.client?.id,
                    legacyCustomerId: data.clientContactId
                };
                const files = this.clearanceForm.get('clearanceFiles') as FormArray;
                files.clear();
                data.clearanceFiles?.forEach(file => files.push(this.fb.control(file)));
                this.containerRows.clear();
                if (data.customsContainers?.length) {
                    data.customsContainers.forEach(container => this.addContainerRow(container));
                } else {
                    this.addContainerRow();
                }
                this.loadCitiesForExistingSelection(data);
                this.isLoading = false;
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error loading clearance header' });
                this.isLoading = false;
            }
        });
    }

    loadShippers(): void {
        const companyId = this.sessionStorageService.getCompany()!.id;
        this.clearanceService.getAllShippersByCompany(companyId, 0, 1000, 'shipperName', 'ASC').subscribe({
            next: data => this.shippers = data.content,
            error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error loading shippers' })
        });
    }

    loadShipperContacts(): void {
        const companyId = this.sessionStorageService.getCompany()!.id;
        this.customerService.getCustomersByCompany(companyId).subscribe({
            next: customers => {
                this.shipperContacts = customers;
                this.filteredShipperContacts = customers;
            },
            error: () => this.shipperContacts = []
        });
    }

    loadCustomsBrokers(): void {
        const companyId = this.sessionStorageService.getCompany()!.id;
        this.partyService.getParties(companyId, [PartyType.FreightForwarder], '', 0, 1000).subscribe({
            next: page => {
                this.customsBrokers = page.content.map(party => ({
                    id: party.id.entityId,
                    name: party.entityName,
                    phoneNumber: party.phoneNumber,
                    emailAddress: party.emailAddress,
                    address: party.address,
                    crNumber: party.crNumber ? Number(party.crNumber) : undefined
                } as FreightForwarder));
            },
            error: () => this.customsBrokers = []
        });
    }

    loadCountries(): void {
        this.countries = this.sessionStorageService?.getCountryList() ?? [];
    }

    saveClearanceHeader(): void {
        if (!this.canManageClearance) {
            this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to save this clearance.' });
            return;
        }
        if (this.clearanceForm.invalid || this.containerRows.invalid) {
            this.clearanceForm.markAllAsTouched();
            this.messageService.add({ severity: 'warn', summary: 'Missing details', detail: 'Complete required clearance, route, delivery, and container details.' });
            return;
        }

        this.isLoading = true;
        const clearanceHeader: CustomsClearanceHeader = this.buildClearanceHeaderPayload();
        clearanceHeader.owningCompanyId = clearanceHeader.owningCompanyId || this.sessionStorageService.getCompany()!.id;
        const sessionCustomerId = this.sessionStorageService.getCustomer()?.id;
        if (!clearanceHeader.owningCompanyContactId && sessionCustomerId) {
            clearanceHeader.owningCompanyContactId = sessionCustomerId;
        }
        clearanceHeader.customsContainers = this.containerRows.value;
        this.syncDerivedRouteText(clearanceHeader);

        if (this.isEditMode) {
            this.clearanceService.updateCustomsClearanceHeader(clearanceHeader.clearanceHeaderID!, clearanceHeader).subscribe({
                next: data => this.afterSave(data, 'Clearance header updated successfully'),
                error: () => this.saveFailed('Error updating clearance header')
            });
        } else {
            this.clearanceService.createCustomsClearanceHeader(clearanceHeader).subscribe({
                next: data => this.afterSave(data, 'Clearance header created successfully'),
                error: () => this.saveFailed('Error creating clearance header')
            });
        }
    }

    onCommercialPartyChange(reference: CommercialPartyReference): void {
        this.commercialPartyReference = reference;
        this.clearanceForm.patchValue({
            crmClientId: reference.crmClientId ?? null,
            crmAccountId: reference.crmAccountId ?? null,
            crmClientContactId: reference.crmContactId ?? null,
            client: reference.legacyCompanyId ? { id: reference.legacyCompanyId } : null,
            clientContactId: reference.legacyCustomerId ?? null
        });
    }

    get owningCompanyId(): number | undefined {
        return this.clearanceForm.get('owningCompanyId')?.value || this.sessionStorageService.getCompany()?.id;
    }

    private buildClearanceHeaderPayload(): CustomsClearanceHeader {
        const { selectedFileTypes, ...payload } = this.clearanceForm.getRawValue();
        return payload as CustomsClearanceHeader;
    }

    private afterSave(data: CustomsClearanceHeader, detail: string): void {
        this.messageService.add({ severity: 'success', summary: 'Success', detail });
        this.linkProjectRequirementFulfillment(data.clearanceHeaderID, data.jobNumber || data.clearanceJobNumber).add(() => {
            this.isLoading = false;
            this.router.navigate(['/clearance/view', data.clearanceHeaderID]);
        });
    }

    private linkProjectRequirementFulfillment(targetResourceId?: number, sourceReference?: string) {
        const projectId = Number(this.route.snapshot.queryParamMap.get('projectId') || this.clearanceForm.get('logisticsProjectId')?.value || 0);
        const requirementId = Number(this.route.snapshot.queryParamMap.get('requirementId') || 0);
        const owningCompanyId = Number(this.clearanceForm.get('owningCompanyId')?.value || this.sessionStorageService.getCompany()?.id || 0);
        if (!projectId || !requirementId || !owningCompanyId || !targetResourceId) {
            return { add: (callback: () => void) => callback() };
        }
        return this.logisticsProjectsService.linkRequirementFulfillment(projectId, owningCompanyId, requirementId, {
            targetResourceType: this.route.snapshot.queryParamMap.get('targetResourceType') || 'CLEARANCE_HEADER',
            targetResourceId,
            sourceModule: 'CLEARANCE',
            sourceReference: sourceReference || `Clearance #${targetResourceId}`,
            fulfilledQuantity: Number(this.route.snapshot.queryParamMap.get('quantity') || 1),
            fulfillmentStatus: 'LINKED',
            status: 'ACTIVE'
        }).subscribe({
            error: error => this.messageService.add({
                severity: 'warn',
                summary: 'Requirement link',
                detail: error?.error?.message || 'Clearance was saved, but requirement fulfillment was not linked.'
            })
        });
    }

    private saveFailed(detail: string): void {
        this.messageService.add({ severity: 'error', summary: 'Error', detail });
        this.isLoading = false;
    }

    cancel(): void {
        this.router.navigate(['/clearance']);
    }

    get containerRows(): FormArray {
        return this.clearanceForm.get('customsContainers') as FormArray;
    }

    addContainerRow(container?: CustomsContainer): void {
        this.containerRows.push(this.fb.group({
            id: [container?.id ?? null],
            containerNo: [container?.containerNo ?? '', Validators.required],
            containerType: [container?.containerType ?? ''],
            sealNo: [container?.sealNo ?? ''],
            grossWeight: [container?.grossWeight ?? null],
            netWeight: [container?.netWeight ?? null],
            remarks: [container?.remarks ?? ''],
            containerCaseId: [container?.containerCaseId ?? null]
        }));
    }

    removeContainerRow(index: number): void {
        if (this.containerRows.length <= 1) {
            this.messageService.add({ severity: 'warn', summary: 'Container required', detail: 'At least one container row is required.' });
            return;
        }
        this.containerRows.removeAt(index);
    }

    onRouteCountryChange(type: 'loading' | 'discharge' | 'customs', countryId: number | null): void {
        this.clearanceForm.get(`${type}CityId`)?.patchValue(null, { emitEvent: false });
        this.loadCities(type, countryId);
    }

    loadCities(type: 'loading' | 'discharge' | 'customs', countryId: number | null | undefined): void {
        if (!countryId) {
            this.setCities(type, []);
            return;
        }
        this.baseMasterDataService.getCities('en-US', Number(countryId)).subscribe(cities => this.setCities(type, cities ?? []));
    }

    openQuickCreate(type: PartyQuickCreateType): void {
        if (type === 'consigneeContact' && !this.clearanceForm.get('consignee')?.value) {
            this.messageService.add({ severity: 'warn', summary: 'Select consignee', detail: 'Select or create a consignee first.' });
            return;
        }
        this.quickCreateType = type;
        this.quickCreateVisible = true;
    }

    onQuickCreateCreated(result: PartyQuickCreateResult): void {
        if (result.type === 'broker') {
            const broker = result.record as FreightForwarder;
            this.customsBrokers = [broker, ...this.customsBrokers];
            this.clearanceForm.get('broker')?.patchValue(broker);
            this.showCreated('Customs broker created');
            return;
        }
        if (result.type === 'shipper') {
            const shipper = result.record as Shipper;
            this.shippers = [shipper, ...this.shippers];
            this.clearanceForm.get('shipper')?.patchValue(shipper);
            this.showCreated('Shipper created');
            return;
        }
        if (result.type === 'consignee') {
            const company = result.record as Company;
            this.consignees = [company, ...this.consignees];
            this.clearanceForm.get('consignee')?.patchValue(company);
            this.showCreated('Company created');
            return;
        }
        if (result.type === 'location') {
            const location = result.record as Location;
            this.clearanceForm.get('deliveryLocation')?.patchValue(location);
            this.showCreated('Delivery location created');
            return;
        }
        const contact = result.record as Customer;
        if (result.type === 'consigneeContact') {
            this.consigneeContacts = [contact, ...this.consigneeContacts];
            this.clearanceForm.get('consigneeContact')?.patchValue(contact);
        } else {
            this.shipperContacts = [contact, ...this.shipperContacts];
            this.filteredShipperContacts = [contact, ...this.filteredShipperContacts];
            this.clearanceForm.get('shipperContact')?.patchValue(contact);
        }
        this.showCreated('Customer contact created');
    }

    uploadedFileForType(fileType: string): CustomsClearanceFile | undefined {
        return (this.clearanceForm.get('clearanceFiles') as FormArray).value
            ?.find((file: CustomsClearanceFile) => file.fileType === fileType);
    }

    removeUploadedFile(fileType: string): void {
        const files = this.clearanceForm.get('clearanceFiles') as FormArray;
        const index = files.value.findIndex((file: CustomsClearanceFile) => file.fileType === fileType);
        if (index >= 0) {
            files.removeAt(index);
        }
    }

    onFileUpload(event: any, fileType: string): void {
        const file: File = event.files[0];
        if (!file) return;
        const newFileName = `${Date.now()}_${file.name}`;
        this.firebaseStorageService.uploadFileWithProgress(file, `clearance-files/${newFileName}`).subscribe({
            next: upload => {
                this.uploadProgressByType[fileType] = upload.progress;
                if (upload.url) {
                    const clearanceFile: CustomsClearanceFile = {
                        headerId: this.clearanceForm.get('clearanceHeaderID')?.value,
                        fileType,
                        fileName: newFileName,
                        fileSize: file.size,
                        fileUrl: upload.url,
                        createdAt: new Date(),
                        createdBy: this.sessionStorageService.getCustomer()!.id,
                        updatedAt: new Date(),
                        updatedBy: this.sessionStorageService.getCustomer()!.id
                    };
                    this.removeUploadedFile(fileType);
                    (this.clearanceForm.get('clearanceFiles') as FormArray).push(this.fb.control(clearanceFile));
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: `${file.name} uploaded.` });
                    this.uploadProgressByType[fileType] = 0;
                }
            },
            error: err => {
                this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail: `${file.name} could not be uploaded: ${err.message}` });
            }
        });
    }

    private showCreated(detail: string): void {
        this.messageService.add({ severity: 'success', summary: 'Created', detail });
        this.quickCreateType = null;
    }

    quickCreateFailed(label: string): void {
        this.messageService.add({ severity: 'error', summary: 'Create failed', detail: `Unable to create ${label}.` });
    }

    defaultCountryId(): number | null {
        return this.sessionStorageService.getCompany()?.countryId ?? this.countries?.[0]?.id ?? null;
    }

    currentCompany(): Company | null {
        return this.sessionStorageService.getCompany() ?? null;
    }

    quickCreateContactCompany(): Company | null {
        return this.quickCreateType === 'consigneeContact'
            ? this.clearanceForm.get('consignee')?.value as Company
            : this.currentCompany();
    }

    private setCities(type: 'loading' | 'discharge' | 'customs', cities: City[]): void {
        if (type === 'loading') this.loadingCities = cities;
        if (type === 'discharge') this.dischargeCities = cities;
        if (type === 'customs') this.customsCities = cities;
    }

    private loadCitiesForExistingSelection(data: CustomsClearanceHeader): void {
        this.loadCities('loading', data.loadingCountryId);
        this.loadCities('discharge', data.dischargeCountryId);
        this.loadCities('customs', data.customsCountryId);
    }

    private syncDerivedRouteText(clearanceHeader: CustomsClearanceHeader): void {
        const loadingCity = this.loadingCities.find(city => city.id === clearanceHeader.loadingCityId);
        const dischargeCity = this.dischargeCities.find(city => city.id === clearanceHeader.dischargeCityId);
        const customsCity = this.customsCities.find(city => city.id === clearanceHeader.customsCityId);
        clearanceHeader.pol = loadingCity?.cityName ?? '';
        clearanceHeader.pod = dischargeCity?.cityName ?? '';
        clearanceHeader.customsLocation = customsCity?.cityName ?? '';
        clearanceHeader.country = this.countries.find(country => country.id === clearanceHeader.customsCountryId)
            ?? this.countries.find(country => country.id === clearanceHeader.dischargeCountryId)
            ?? this.countries.find(country => country.id === clearanceHeader.loadingCountryId) as Country;
    }
}
